import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { propsOf, strProp, arrProp, isOneOfService } from './_graphHelpers';

/**
 * P0.3 — cross-account admin role.
 * Severity: critical. SOC 2 CC6.3 / CC6.6.
 *
 * Fires when an IAM role has an effective `*:*` policy AND a trust
 * policy that permits `sts:AssumeRole` from a non-prod account. We are
 * deliberately conservative — "effective admin" is derived from
 * `effectivePermissions` or a flattened policy action list present on
 * the resource. Without the effective-permissions attribute, the rule
 * stays quiet to avoid false positives.
 */
export const crossAccountAdminRoleRule: FindingRule = {
  id: 'cross_account_admin_role',
  label: 'Cross-account admin role',
  description: 'IAM role with *:* assumable from a non-prod account.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      if (!isOneOfService(node, ['iam-role', 'role'])) continue;
      const p = propsOf(node);
      const effective = (arrProp(p, ['effectivePermissions']) ??
        arrProp(p, ['policyActions']) ??
        []) as unknown[];
      const hasWildcard = effective.some((a) => a === '*:*' || a === '*');
      if (!hasWildcard) continue;

      const trustedAccounts = (arrProp(p, ['trustedAccounts']) ??
        arrProp(p, ['assumeRolePolicy', 'trustedAccounts']) ??
        []) as unknown[];
      const ownAccount = strProp(p, ['accountId']);
      const fromNonProd = trustedAccounts.some((acc) => {
        if (typeof acc !== 'string') return false;
        if (ownAccount && acc === ownAccount) return false;
        // Treat any account with a name/tag containing 'dev', 'stage',
        // 'test', 'sandbox' as non-prod. When we only have the id, we
        // still flag — auditors want the cross-account hop called out
        // regardless of environment label.
        return true;
      });
      if (!fromNonProd) continue;

      findings.push({
        id: `cross_account_admin_role:resource:${node.id}`,
        ruleId: 'cross_account_admin_role',
        severity: 'critical',
        scope: 'resource',
        subject: { kind: 'resource', id: node.id },
        title: 'Cross-account admin role',
        detail: `${String(node.label ?? node.id)} grants *:* and is assumable from ${trustedAccounts.length} other account(s).`,
        attrs: { trustedAccounts },
      });
    }
    return findings;
  },
};
